// src/features/executive/export/use-export-pdf.ts
// Phase 1F PDF export: window.print() with temporary document.title override
// so the browser's "Save as PDF" dialog defaults to our filename.

import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { ExecutiveTab } from 'api/executive/executive.types'
import { getClinicInfo } from 'utils/app.utils'
import { buildPdfFilename } from './format'
import { swalError } from 'component/Alert/Swal'

/** ms to wait before forcing title restore when afterprint does not fire (Safari cancel) */
const PRINT_TITLE_RESTORE_DELAY_MS = 10_000

export function useExportPdf() {
  const { t } = useTranslation()
  const [error, setError] = useState<Error | null>(null)

  const exportTab = useCallback((tab: ExecutiveTab) => {
    setError(null)
    try {
      const branchInfos = getClinicInfo()
      const clinicDisplayName = branchInfos?.clinicName || branchInfos?.clinicNameEn || ''
      const prevTitle = document.title
      const targetTitle = buildPdfFilename(tab, clinicDisplayName)
      document.title = targetTitle

      const restore = () => {
        document.title = prevTitle
        window.removeEventListener('afterprint', restore)
      }
      window.addEventListener('afterprint', restore)

      // Safari does not fire afterprint reliably if print is cancelled.
      // Fallback: restore title after a short delay regardless.
      setTimeout(() => {
        if (document.title === targetTitle) document.title = prevTitle
        window.removeEventListener('afterprint', restore)
      }, PRINT_TITLE_RESTORE_DELAY_MS)

      window.print()
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e))
      setError(err)
      console.error('[Phase 1F] PDF export failed', err)
      swalError(t('EXECUTIVE.EXPORT.ERROR'))
    }
  }, [t])

  return { exportTab, error }
}
